import { AnimatePresence, motion } from "motion/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Icon from "../../../lib/components/custom/Icon";
import { Badge } from "../../../lib/components/ui/badge";
import { Button, buttonVariants } from "../../../lib/components/ui/button";
import { Card } from "../../../lib/components/ui/card";
import { Dialog, DialogTrigger } from "../../../lib/components/ui/dialog";
import SearchInput from "../../../lib/components/ui/search-input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../lib/components/ui/select";
import { Skeleton } from "../../../lib/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "../../../lib/components/ui/tabs";
import { useApi } from "../../../lib/hooks/api/use-api";
import { useAdminStore } from "../../../lib/hooks/use-store";
import { cn } from "../../../lib/utils";
import AdminOrderCard from "../AdminOrderCard";
import AdminLayout from "../layout";
import OrderDetailsDialog from "../OrderDetailsDialog";

const fadeInUp = {
	initial: { opacity: 0, y: 10 },
	animate: { opacity: 1, y: 0 },
	transition: { duration: 0.4, ease: "easeOut" },
};

const staggerContainer = {
	animate: {
		transition: {
			staggerChildren: 0.05,
		},
	},
};

export default function AdminOrders() {
	const { t } = useTranslation();
	const [searchTerm, setSearchTerm] = useState("");
	const [statusFilter, setStatusFilter] = useState<string | "all">("all");
	const [sortBy, setSortBy] = useState<"newest" | "oldest">("newest");
	const [updatingOrder, setUpdatingOrder] = useState<number | null>(null);
	const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
	const [viewMode, setViewMode] = useState<"list" | "grid">("grid");

	const { orders } = useApi();
	const { showTodayOnly, setShowTodayOnly } = useAdminStore();

	// Helper functions
	const getStatusColor = (status: string, paymentStatus?: string) => {
		// Special case for cancelled orders due to failed payment
		if (status === "cancelled" && paymentStatus === "failed") {
			return "bg-red-100 text-red-800 border-red-300 ring-1 ring-red-200";
		}
		// Pending = Stripe has not confirmed payment (danger)
		if (status === "pending") {
			return "bg-red-100 text-red-800 border-red-200";
		}

		switch (status) {
			case "received":
				return "bg-destructive/10 text-destructive border-destructive/20";
			case "confirmed":
				return "bg-blue-100 text-blue-800 border-blue-200";
			case "preparing":
				return "bg-orange-100 text-orange-800 border-orange-200";
			case "ready":
				return "bg-green-100 text-green-800 border-green-200";
			case "out-for-delivery":
				return "bg-purple-100 text-purple-800 border-purple-200";
			case "delivered":
				return "bg-gray-100 text-gray-800 border-gray-200";
			case "cancelled":
				return "bg-red-100 text-red-800 border-red-200";
			default:
				return "bg-gray-100 text-gray-800 border-gray-200";
		}
	};

	const getStatusText = (status: string, paymentStatus?: string) => {
		// Special case for cancelled orders due to failed payment
		if (status === "cancelled" && paymentStatus === "failed") {
			return t("admin.order_details.payment_failed");
		}
		if (status === "pending") return t("admin.dashboard.status.pending_unpaid");

		switch (status) {
			case "received":
				return t("admin.dashboard.status.new_order");
			case "confirmed":
				return t("admin.dashboard.status.confirmed");
			case "preparing":
				return t("admin.dashboard.status.preparing");
			case "ready":
				return t("admin.dashboard.status.ready");
			case "out-for-delivery":
				return t("admin.dashboard.status.out_for_delivery");
			case "delivered":
				return t("admin.dashboard.status.delivered");
			case "cancelled":
				return t("admin.dashboard.status.cancelled");
			default:
				return status;
		}
	};

	const formatDateTime = (dateString: string) => {
		return new Date(dateString).toLocaleString([], {
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	// Fetch order statuses from API
	const { data: orderStatusesData } = orders.getStatuses();

	// Generate status options from API (using raw values, no translation)
	const statusOptions = useMemo(() => {
		const options: { value: string | "all"; label: string }[] = [
			{ value: "all", label: t("admin.orders.all_status") },
		];
		if (orderStatusesData?.statuses) {
			orderStatusesData.statuses.forEach((status) => {
				// Use raw status value as label (no translation)
				options.push({
					value: status,
					label: status,
				});
			});
		}
		return options;
	}, [orderStatusesData, t]);

	// Fetch all orders
	const {
		data: ordersData,
		isLoading,
		isRefetching,
		error,
		refetch,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = orders.getAllInfinite({ limit: 10 });

	const loadMore = () => {
		if (hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	};

	const filteredOrders = useMemo(() => {
		if (!ordersData?.pages) return [];

		// Flatten all pages
		const allOrders = ordersData.pages.flatMap((page) => page.orders || []);

		const filtered = allOrders.filter((order) => {
			// Filter by today's orders if toggle is on
			const today = new Date();
			const todayStart = new Date(
				today.getFullYear(),
				today.getMonth(),
				today.getDate(),
			);
			const orderDate = new Date(order.createdAt);
			const isTodayOrder = showTodayOnly ? orderDate >= todayStart : true;

			const matchesSearch =
				order.id.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
				(order.user?.name || "")
					.toLowerCase()
					.includes(searchTerm.toLowerCase()) ||
				(order.user?.email || "")
					.toLowerCase()
					.includes(searchTerm.toLowerCase());

			const matchesStatus =
				statusFilter === "all" || order.status === statusFilter;

			return matchesSearch && matchesStatus && isTodayOrder;
		});

		// Define status priority for today's orders (active orders first, completed last)
		const getStatusPriority = (status: string) => {
			const activeStatuses = [
				"received",
				"confirmed",
				"preparing",
				"ready",
				"out-for-delivery",
			];
			const completedStatuses = ["delivered", "cancelled"];

			if (activeStatuses.includes(status)) return 1; // Active orders
			if (completedStatuses.includes(status)) return 2; // Completed orders
			return 3; // Unknown status
		};

		// Sort orders
		filtered.sort((a, b) => {
			// First priority: status grouping (active orders before completed)
			const priorityA = getStatusPriority(a.status);
			const priorityB = getStatusPriority(b.status);

			if (priorityA !== priorityB) {
				return priorityA - priorityB;
			}

			// Second priority: scheduled orders come first, sorted by scheduledFor time
			const aScheduled = a.scheduledFor
				? new Date(a.scheduledFor).getTime()
				: null;
			const bScheduled = b.scheduledFor
				? new Date(b.scheduledFor).getTime()
				: null;

			// If one is scheduled and the other isn't, scheduled comes first
			if (aScheduled && !bScheduled) return -1;
			if (!aScheduled && bScheduled) return 1;

			// If both are scheduled, sort by scheduledFor time
			if (aScheduled && bScheduled) {
				return sortBy === "newest"
					? aScheduled - bScheduled // Earlier scheduled times first
					: bScheduled - aScheduled; // Later scheduled times first
			}

			// Third priority: non-scheduled orders sorted by createdAt
			if (sortBy === "newest") {
				return (
					new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
				);
			} else {
				return (
					new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
				);
			}
		});

		return filtered;
	}, [ordersData, searchTerm, statusFilter, sortBy, showTodayOnly]);

	const handleStatusUpdate = async (
		orderId: number,
		newStatus:
			| "received"
			| "pending"
			| "preparing"
			| "out-for-delivery"
			| "delivered"
			| "cancelled",
	) => {
		setUpdatingOrder(orderId);
		try {
			await orders.updateStatus.mutateAsync({
				orderId: orderId.toString(),
				status: newStatus,
			});
			toast.success(
				t("admin.orders.order_moved_to", { status: getStatusText(newStatus) }),
			);
			setSelectedOrderId(null);
			refetch();
		} catch (error) {
			console.error(error);
			toast.error(t("admin.orders.failed_to_update"));
		} finally {
			setUpdatingOrder(null);
		}
	};

	// Error handling
	if (error) {
		return (
			<AdminLayout>
				<div className="flex items-center justify-center min-h-[400px]">
					<div className="text-center">
						<div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
							<Icon name="X" className="w-6 h-6 text-destructive" />
						</div>
						<h3 className="text-lg font-semibold mb-2">
							{t("admin.orders.error_title")}
						</h3>
						<p className="text-muted-foreground mb-4">
							{error.message || t("admin.orders.error_description")}
						</p>
						<Button onClick={() => refetch()}>
							{t("admin.orders.try_again")}
						</Button>
					</div>
				</div>
			</AdminLayout>
		);
	}

	return (
		<AdminLayout>
			<div className="space-y-6">
				{/* Minimal Header with Filters */}
				<div className="flex flex-col gap-6 pb-2 border-b border-border/40">
					<div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
						<motion.div
							initial={{ opacity: 0, x: -10 }}
							animate={{ opacity: 1, x: 0 }}
							className="flex flex-col gap-1"
						>
							<h1 className="text-2xl font-bold tracking-tight text-foreground">
								{t("admin.orders.title")}
							</h1>
							<p className="text-sm text-muted-foreground">
								{t("admin.orders.subtitle")}
							</p>
						</motion.div>

						<motion.div
							initial={{ opacity: 0, x: 10 }}
							animate={{ opacity: 1, x: 0 }}
							className=""
						>
							<div className="flex items-center justify-center">
								<Tabs
									value={showTodayOnly ? "today" : "all"}
									onValueChange={(value) => setShowTodayOnly(value === "today")}
								>
									<TabsList className="grid w-full grid-cols-2">
										<TabsTrigger
											value="today"
											className="text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
										>
											{t("admin.orders.today")}
										</TabsTrigger>
										<TabsTrigger
											value="all"
											className="text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
										>
											{t("admin.orders.all_time")}
										</TabsTrigger>
									</TabsList>
								</Tabs>
								<div className="h-4 w-px bg-border mx-1" />
								<div className="flex bg-muted rounded-md p-1">
									<Button
										variant={viewMode === "list" ? "primary" : "ghost"}
										size="sm"
										className="h-7 w-7 p-0"
										onClick={() => setViewMode("list")}
									>
										<Icon name="List" className="w-4 h-4" />
									</Button>
									<Button
										variant={viewMode === "grid" ? "primary" : "ghost"}
										size="sm"
										className="h-7 w-7 p-0"
										onClick={() => setViewMode("grid")}
									>
										<Icon name="LayoutGrid" className="w-4 h-4" />
									</Button>
								</div>
							</div>
						</motion.div>
					</div>

					{/* Filters Bar */}
					<motion.div
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.1 }}
						className="flex flex-col lg:flex-row gap-4 items-center"
					>
						<div className="w-full lg:max-w-md">
							<SearchInput
								placeholder={t("admin.orders.search_placeholder")}
								onSearchChange={setSearchTerm}
								className="w-full"
							/>
						</div>

						<div className="flex flex-wrap sm:flex-nowrap gap-3 items-center w-full lg:w-auto ml-auto">
							<Select
								value={statusFilter}
								onValueChange={(value) =>
									setStatusFilter(value as string | "all")
								}
							>
								<SelectTrigger className="w-full sm:w-44 bg-card">
									<SelectValue
										placeholder={t("admin.orders.filter_by_status")}
									/>
								</SelectTrigger>
								<SelectContent>
									{statusOptions.map((option) => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>

							<Select
								value={sortBy}
								onValueChange={(value) =>
									setSortBy(value as "newest" | "oldest")
								}
							>
								<SelectTrigger className="w-full sm:w-36 bg-card">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="newest">
										{t("admin.orders.newest_first")}
									</SelectItem>
									<SelectItem value="oldest">
										{t("admin.orders.oldest_first")}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
					</motion.div>
				</div>

				{/* Orders Content */}
				<div className="flex-1 min-h-[500px] relative">
					{(isRefetching || isLoading) && filteredOrders.length > 0 && (
						<div className="absolute inset-0 bg-background/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
							<div className="flex flex-col items-center gap-2">
								<Icon
									name="Loader"
									className="w-6 h-6 animate-spin text-primary"
								/>
								<span className="text-sm text-muted-foreground">
									{t("admin.orders.updating")}
								</span>
							</div>
						</div>
					)}

					<div className="flex items-center justify-between mb-4">
						<span className="text-sm text-muted-foreground">
							{t("admin.orders.showing_orders", {
								count: filteredOrders.length,
							})}
							{(isLoading || isRefetching) && (
								<span className="inline-flex items-center gap-2 ml-2">
									<Icon name="Loader" className="w-3 h-3 animate-spin" />{" "}
									{t("admin.orders.updating")}
								</span>
							)}
						</span>
					</div>

					{isLoading && filteredOrders.length === 0 ? (
						<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
							{Array.from({ length: 6 }).map((_, i) => (
								<Card
									key={i}
									className="p-6 space-y-4 rounded-xl border-border/50"
								>
									<div className="flex justify-between">
										<Skeleton className="h-4 w-24" />
										<Skeleton className="h-6 w-20 rounded-full" />
									</div>
									<Skeleton className="h-12 w-full rounded-lg" />
									<div className="space-y-2">
										<Skeleton className="h-4 w-full" />
										<Skeleton className="h-4 w-2/3" />
									</div>
									<div className="pt-4 border-t flex justify-between">
										<Skeleton className="h-8 w-20" />
										<Skeleton className="h-8 w-24" />
									</div>
								</Card>
							))}
						</div>
					) : filteredOrders.length === 0 ? (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							className="flex flex-col items-center justify-center p-20 bg-card rounded-xl border border-dashed"
						>
							<div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
								<Icon
									name="Package"
									className="size-8 text-muted-foreground/50"
								/>
							</div>
							<h3 className="text-lg font-semibold mb-2">
								{t("admin.orders.no_orders_found")}
							</h3>
							<p className="text-muted-foreground text-center max-w-sm">
								{searchTerm || statusFilter !== "all"
									? t("admin.orders.no_orders_filtered")
									: showTodayOnly
										? t("admin.orders.no_orders_today")
										: t("admin.orders.no_orders_ever")}
							</p>
						</motion.div>
					) : (
						<motion.div
							variants={staggerContainer}
							initial="initial"
							animate="animate"
							// Re-animate when filters change
							key={`${statusFilter}-${searchTerm}-${sortBy}-${showTodayOnly}`}
							className={cn(
								viewMode === "grid"
									? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
									: "flex flex-col gap-3",
							)}
						>
							<AnimatePresence mode="popLayout" initial={false}>
								{filteredOrders.map((order) => (
									<Dialog
										key={order.id}
										open={selectedOrderId === order.id}
										onOpenChange={(open) =>
											setSelectedOrderId(open ? order.id : null)
										}
									>
										<DialogTrigger asChild>
											<motion.div
												variants={fadeInUp}
												initial="initial"
												animate="animate"
												whileHover={{
													y: viewMode === "grid" ? -4 : -2,
													transition: { duration: 0.2 },
												}}
												className={cn(
													"cursor-pointer group relative overflow-hidden",
													viewMode === "grid"
														? "bg-card rounded-2xl border shadow-sm hover:shadow-md hover:border-primary/20 transition-all duration-300"
														: "bg-card rounded-xl border shadow-sm hover:shadow-md hover:border-primary/20 transition-all duration-300",
												)}
											>
												{/* Grid View Card */}
												{viewMode === "grid" ? (
													<AdminOrderCard
														orderId={order.id}
														initialOrderData={order}
														onClick={() => setSelectedOrderId(order.id)}
														getStatusColor={getStatusColor}
														getStatusText={getStatusText}
														formatDateTime={formatDateTime}
													/>
												) : (
													/* List View Row */
													<div className="p-4 flex items-center gap-4">
														<div className="shrink-0 w-16 h-16 bg-muted/50 rounded-lg flex flex-col items-center justify-center border text-center">
															<span className="text-xs text-muted-foreground font-mono">
																#{order.id}
															</span>
														</div>

														<div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
															<div className="md:col-span-1">
																<h3 className="font-semibold text-base truncate">
																	{order.user?.name ||
																		t("admin.order_card.unknown_customer")}
																</h3>
																<p className="text-sm text-muted-foreground truncate">
																	{order.user?.email}
																</p>
															</div>

															<div className="md:col-span-1">
																<div className="flex items-center gap-2 text-sm text-muted-foreground">
																	<Icon
																		name="MapPin"
																		className="w-4 h-4 shrink-0"
																	/>
																	<span className="truncate max-w-[200px]">
																		{order.address?.streetAddress}
																	</span>
																</div>
																<div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
																	<Icon
																		name={
																			order.scheduledFor ? "Calendar" : "Clock"
																		}
																		className="w-3 h-3 shrink-0"
																	/>
																	{order.scheduledFor
																		? (() => {
																				try {
																					const scheduledDate = new Date(
																						order.scheduledFor,
																					);
																					if (
																						Number.isNaN(
																							scheduledDate.getTime(),
																						)
																					)
																						return formatDateTime(
																							order.createdAt,
																						);
																					return `Scheduled: ${scheduledDate.toLocaleTimeString(
																						[],
																						{
																							hour: "numeric",
																							minute: "2-digit",
																							hour12: true,
																						},
																					)}`;
																				} catch {
																					return formatDateTime(
																						order.createdAt,
																					);
																				}
																			})()
																		: formatDateTime(order.createdAt)}
																</div>
															</div>

															<div className="md:col-span-1 flex items-center justify-start md:justify-center gap-2 flex-wrap">
																{order.scheduledFor &&
																	(() => {
																		try {
																			const scheduledDate = new Date(
																				order.scheduledFor,
																			);
																			if (Number.isNaN(scheduledDate.getTime()))
																				return null;
																			return (
																				<Badge
																					variant="outline"
																					className="text-xs font-medium px-2 py-0.5 bg-secondary/10 text-secondary border-secondary/20"
																				>
																					<Icon
																						name="Clock"
																						className="w-3 h-3 mr-1"
																					/>
																					{scheduledDate.toLocaleTimeString(
																						[],
																						{
																							hour: "numeric",
																							minute: "2-digit",
																							hour12: true,
																						},
																					)}
																				</Badge>
																			);
																		} catch {
																			return null;
																		}
																	})()}
																<Badge
																	className={cn(
																		"text-xs font-medium px-2.5 py-1",
																		getStatusColor(order.status),
																	)}
																>
																	{getStatusText(order.status)}
																</Badge>
															</div>

															<div className="md:col-span-1 text-right">
																<p className="font-bold text-lg text-primary">
																	€{Number(order.totalPrice).toFixed(2)}
																</p>
																<p
																	className={`text-xs font-medium ${order.paymentMethod === "cash" ? "text-amber-600" : "text-blue-600"}`}
																>
																	{order.paymentMethod === "cash"
																		? t("admin.order_card.cash_on_delivery")
																		: t("admin.order_card.card")}
																</p>
															</div>
														</div>

														<div className="shrink-0 ml-2">
															<Button
																variant="ghost"
																size="icon"
																className="text-muted-foreground hover:text-primary"
															>
																<Icon name="ChevronRight" className="w-5 h-5" />
															</Button>
														</div>
													</div>
												)}
											</motion.div>
										</DialogTrigger>

										<OrderDetailsDialog
											orderId={order.id}
											onStatusUpdate={handleStatusUpdate}
											confirmingOrder={updatingOrder}
										/>
									</Dialog>
								))}
							</AnimatePresence>

							{hasNextPage && (
								<motion.div
									className={cn(
										"flex justify-center mt-8 pb-8",
										viewMode === "grid" ? "col-span-full" : "",
									)}
									initial={{ opacity: 0, y: 10 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ delay: 0.2 }}
								>
									<motion.button
										onClick={loadMore}
										className={cn(buttonVariants({ variant: "primary" }))}
										disabled={isFetchingNextPage}
										whileHover={{ y: -2 }}
										whileTap={{ y: 1 }}
									>
										{isFetchingNextPage ? (
											<>
												<Icon name="Loader" className="size-4 animate-spin" />
												{t("common.loading")}
											</>
										) : (
											<>
												<Icon name="ArrowDown" className="size-4" />
												{t("home.browse.load_more")}
											</>
										)}
									</motion.button>
								</motion.div>
							)}
						</motion.div>
					)}
				</div>
			</div>
		</AdminLayout>
	);
}
